import {
  cityRecordToBirthPlace,
  ensureCitySearchReady,
  formatCityLabel,
  searchCitiesInCatalog,
  type CityRecord,
} from '@/utils/city-catalog';
import type { SoulMapBirthPlace } from '@/utils/soulmap-profile';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const LIST_MAX_HEIGHT = 240;
/** Gap between the input and the list, plus breathing room at the clip edge. */
const LIST_OFFSET = 4;
const LIST_EDGE_MARGIN = 8;

interface ListPlacement {
  side: 'below' | 'above';
  maxHeight: number;
}

/**
 * The part of the screen the list can actually be seen in: the visual
 * viewport (which shrinks under a mobile keyboard), cut down by every
 * scrolling ancestor, such as the modal body.
 */
function getVisibleBounds(element: HTMLElement) {
  const viewport = window.visualViewport;
  let top = viewport?.offsetTop ?? 0;
  let bottom = viewport
    ? viewport.offsetTop + viewport.height
    : window.innerHeight;

  // Stop at <body>: the viewport already bounds the page, and a modal sets
  // overflow: hidden on it without making it a meaningful clip box.
  for (
    let node = element.parentElement;
    node && node !== document.body;
    node = node.parentElement
  ) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'visible') continue;
    const rect = node.getBoundingClientRect();
    top = Math.max(top, rect.top);
    bottom = Math.min(bottom, rect.bottom);
  }

  return { top, bottom };
}

interface BirthPlaceAutocompleteProps {
  value: SoulMapBirthPlace | null;
  onChange: (place: SoulMapBirthPlace | null) => void;
  disabled?: boolean;
  error?: string;
  /**
   * A stored place is being looked up in the catalog. The field is held while
   * it runs, so an arriving value cannot overwrite what the user has typed.
   */
  isResolving?: boolean;
}

export function BirthPlaceAutocomplete({
  value,
  onChange,
  disabled = false,
  error,
  isResolving = false,
}: BirthPlaceAutocompleteProps) {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRequestIdRef = useRef(0);
  const [inputValue, setInputValue] = useState(value?.label ?? '');
  const [suggestions, setSuggestions] = useState<CityRecord[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [placement, setPlacement] = useState<ListPlacement>({
    side: 'below',
    maxHeight: LIST_MAX_HEIGHT,
  });

  const isListVisible = isOpen && !isSearching && suggestions.length > 0;

  /** Opens below when it fits, otherwise on whichever side has more room. */
  const updatePlacement = useCallback(() => {
    const anchor = anchorRef.current;
    const list = listRef.current;
    if (!anchor || !list) return;

    const anchorRect = anchor.getBoundingClientRect();
    const bounds = getVisibleBounds(anchor);
    const reserve = LIST_OFFSET + LIST_EDGE_MARGIN;
    const spaceBelow = bounds.bottom - anchorRect.bottom - reserve;
    const spaceAbove = anchorRect.top - bounds.top - reserve;
    const wanted = Math.min(list.scrollHeight, LIST_MAX_HEIGHT);

    const side =
      spaceBelow >= wanted || spaceBelow >= spaceAbove ? 'below' : 'above';
    const room = side === 'below' ? spaceBelow : spaceAbove;

    const maxHeight = Math.max(0, Math.min(LIST_MAX_HEIGHT, room));
    // Runs on every scroll, including the list's own; skip no-op renders.
    setPlacement((current) =>
      current.side === side && current.maxHeight === maxHeight
        ? current
        : { side, maxHeight }
    );
  }, []);

  useLayoutEffect(() => {
    if (!isListVisible) return;

    updatePlacement();

    const viewport = window.visualViewport;
    // Capture catches scrolls inside the modal body as well as the page.
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);
    viewport?.addEventListener('resize', updatePlacement);
    viewport?.addEventListener('scroll', updatePlacement);
    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
      viewport?.removeEventListener('resize', updatePlacement);
      viewport?.removeEventListener('scroll', updatePlacement);
    };
  }, [isListVisible, suggestions, updatePlacement]);

  useEffect(() => {
    setInputValue(value?.label ?? '');
  }, [value?.label]);

  useEffect(() => {
    let cancelled = false;

    void ensureCitySearchReady()
      .then(() => {
        if (cancelled) return;
        setLoadError(null);
        setIsReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Could not load city search.';
        setLoadError(message);
        setIsReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isReady || disabled) {
      setSuggestions([]);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (value && inputValue.trim() === value.label) {
        setSuggestions([]);
        setIsOpen(false);
        setIsSearching(false);
        return;
      }

      const normalizedInput = inputValue.trim();
      if (normalizedInput.length < 2) {
        setSuggestions([]);
        setIsOpen(false);
        setIsSearching(false);
        return;
      }

      const requestId = searchRequestIdRef.current + 1;
      searchRequestIdRef.current = requestId;
      setIsSearching(true);
      setIsOpen(false);

      void searchCitiesInCatalog(normalizedInput)
        .then((nextSuggestions) => {
          if (searchRequestIdRef.current !== requestId) return;
          setSuggestions(nextSuggestions);
          setIsOpen(nextSuggestions.length > 0);
          setHighlightedIndex(nextSuggestions.length > 0 ? 0 : -1);
          setIsSearching(false);
        })
        .catch((err: unknown) => {
          if (searchRequestIdRef.current !== requestId) return;
          const message =
            err instanceof Error ? err.message : 'City search failed.';
          setLoadError(message);
          setSuggestions([]);
          setIsOpen(false);
          setIsSearching(false);
        });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [disabled, inputValue, isReady, value]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const selectCity = (city: CityRecord) => {
    const place = cityRecordToBirthPlace(city);
    setInputValue(place.label);
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(place);
  };

  const handleInputChange = (nextValue: string) => {
    setInputValue(nextValue);
    if (value && nextValue.trim() !== value.label) {
      onChange(null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || isSearching || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((current) =>
        current + 1 >= suggestions.length ? 0 : current + 1
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((current) =>
        current - 1 < 0 ? suggestions.length - 1 : current - 1
      );
      return;
    }

    if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      const city = suggestions[highlightedIndex];
      if (city) selectCity(city);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="sm-profile-field" ref={rootRef}>
      <label className="sm-profile-label" htmlFor="soulmap-birth-place">
        City of birth
      </label>
      <div className="sm-city-combobox" ref={anchorRef}>
        <span className="sm-profile-input-wrap">
          <input
            id="soulmap-birth-place"
            type="text"
            className="sm-profile-input"
            placeholder={`${isResolving ? 'Finding your saved city…' : 'Start typing a city…'}`}
            value={inputValue}
            onChange={(event) => handleInputChange(event.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled || isResolving || Boolean(loadError)}
            autoComplete="off"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-busy={!isReady || isSearching || isResolving}
          />
          {(isResolving || isSearching) && (
            <span className="sm-field-spinner sm-spin" aria-hidden="true" />
          )}
        </span>
        {isListVisible && (
          <ul
            id={listboxId}
            ref={listRef}
            className={
              placement.side === 'above'
                ? 'sm-city-suggestions sm-city-suggestions-above'
                : 'sm-city-suggestions'
            }
            style={{ maxHeight: placement.maxHeight }}
            role="listbox"
          >
            {suggestions.map((city, index) => (
              <li key={city.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={
                    index === highlightedIndex
                      ? 'sm-city-suggestion sm-city-suggestion-active'
                      : 'sm-city-suggestion'
                  }
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCity(city)}
                >
                  {formatCityLabel(city)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {isResolving && !loadError && (
        <p className="sm-profile-hint">Finding your saved city…</p>
      )}
      {!isReady && !isResolving && !loadError && (
        <p className="sm-profile-hint">Loading city search…</p>
      )}
      {isSearching && isReady && !isResolving && !loadError && (
        <p className="sm-profile-hint">Searching cities…</p>
      )}
      {loadError && <p className="sm-profile-error">{loadError}</p>}
      {error && !loadError && <p className="sm-profile-error">{error}</p>}
      {!loadError && isReady && !isSearching && !isResolving && (
        <p className="sm-profile-hint">
          Choose a city from the list so we can chart your birth coordinates.
        </p>
      )}
    </div>
  );
}
